(() => {
  const jsonHeaders = { "Content-Type": "application/json" };
  const BUTTON_SPINNER_MARKUP = `
    <div class="loading__spinner hidden" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">
        <circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle>
      </svg>
    </div>
  `;

  function normalizeApiBase(value) {
    const raw = String(value || "/apps/open-baskets").trim();
    return raw.replace(/\/v1\/?$/i, "").replace(/\/+$/, "") || "/apps/open-baskets";
  }
  function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  }
  function formatMoney(value, currencyCode) {
    const amount = Number(value || 0);
    const currency = currencyCode || "USD";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);
  }
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  function toRequestError(response, payload) {
    const fallback = `Request failed (${response.status})`;
    const apiMessage = payload && (payload.error || payload.message);
    if (apiMessage) return new Error(apiMessage);
    if (response.status === 404) {
      return new Error("Reserved baskets is temporarily unavailable (404). Please refresh and try again in a moment.");
    }
    if (response.status >= 500) {
      return new Error("Reserved baskets is temporarily unavailable. Please try again in a moment.");
    }
    return new Error(fallback);
  }
  async function fetchJson(url, options = {}) {
    const method = String(options.method || "GET").toUpperCase();
    const retries = Number.isInteger(options.retries) ? options.retries : method === "GET" ? 1 : 0;
    let attempt = 0;
    while (attempt <= retries) {
      try {
        const response = await fetch(url, options);
        const contentType = String(response.headers.get("content-type") || "").toLowerCase();
        const isJson = contentType.includes("application/json");
        let payload = {};
        let rawText = "";

        if (isJson) {
          payload = await response.json().catch(() => ({}));
        } else {
          rawText = await response.text().catch(() => "");
          payload = {};
        }

        if (!isJson && rawText) {
          const looksLikeAppProxyError = /third-party application/i.test(rawText);
          if (looksLikeAppProxyError) {
            throw new Error("Reserved baskets is temporarily unavailable. Please try again in a moment.");
          }
        }

        if (!response.ok) {
          const err = toRequestError(response, payload);
          const isRetryable = method === "GET" && (response.status === 404 || response.status >= 500);
          if (isRetryable && attempt < retries) {
            attempt += 1;
            await delay(350);
            continue;
          }
          throw err;
        }
        if (payload && typeof payload === "object" && (payload.error || payload.ok === false)) {
          throw toRequestError(response, payload);
        }
        return payload;
      } catch (error) {
        const isNetworkError = !(error instanceof Error) || error.name === "TypeError";
        if (method === "GET" && isNetworkError && attempt < retries) {
          attempt += 1;
          await delay(350);
          continue;
        }
        throw error;
      }
    }
  }
  function showMessage(container, message, type) {
    if (!container) return;
    if (!message) {
      container.hidden = true;
      container.textContent = "";
      container.className = "open-baskets-alert";
      return;
    }
    container.hidden = false;
    container.textContent = message;
    container.className = `open-baskets-alert open-baskets-alert--${type || "info"}`;
  }
  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
  function escapeAttribute(value) {
    return escapeHtml(value);
  }
  function getErrorMessage(error, fallback = "Something went wrong.") {
    return error instanceof Error ? error.message : fallback;
  }
  function showDuplicateBasketNamePopup(error) {
    const message = getErrorMessage(error, "");
    if (!/already exists for your account/i.test(message)) return false;
    window.alert(message);
    return true;
  }
  function defaultBasketName(baskets, fallbackName) {
    const normalizedFallback = String(fallbackName || "").trim();
    if (normalizedFallback) return normalizedFallback;
    const list = Array.isArray(baskets) ? baskets : [];
    let maxNumber = 0;
    list.forEach((basket) => {
      const rawName = String((basket && basket.name) || "").trim();
      const match = rawName.match(/^Basket\s*#\s*(\d+)$/i);
      if (!match) return;
      const value = Number(match[1]);
      if (Number.isFinite(value)) maxNumber = Math.max(maxNumber, value);
    });
    const next = Math.max(maxNumber + 1, list.length + 1, 1);
    return `Basket #${next}`;
  }
  function closeModal(modal) {
    if (modal && typeof modal.close === "function") modal.close();
  }
  function ensureButtonSpinner(button) {
    if (!button || !(button instanceof Element) || !button.classList.contains("button")) return;
    if (button.querySelector(".loading__spinner")) return;
    button.insertAdjacentHTML("beforeend", BUTTON_SPINNER_MARKUP);
  }
  function setButtonLoading(button, isLoading, loadingClass) {
    if (!button) return;
    ensureButtonSpinner(button);
    if (loadingClass) button.classList.toggle(loadingClass, isLoading);
    const spinner = button.querySelector(".loading__spinner");
    if (spinner) {
      button.classList.toggle("loading", isLoading);
      spinner.classList.toggle("hidden", !isLoading);
    }
    button.disabled = isLoading;
    button.setAttribute("aria-busy", isLoading ? "true" : "false");
  }
  async function runButtonAction(button, action, onError, loadingClass) {
    setButtonLoading(button, true, loadingClass);
    try {
      await action();
    } catch (error) {
      if (typeof onError === "function") onError(error);
    } finally {
      setButtonLoading(button, false, loadingClass);
    }
  }
  function renderProductSummaryCell(item) {
    const title = escapeHtml(item?.title || "Item");
    const variantTitle = String(item?.variantTitle || "").trim();
    const variantHtml = variantTitle ? `<div class="caption">${escapeHtml(variantTitle)}</div>` : "";
    if (item?.productUrl) {
      return `<a href="${escapeAttribute(item.productUrl)}" class="open-baskets-table__product-link">${title}</a>${variantHtml}`;
    }
    return `<div>${title}</div>${variantHtml}`;
  }
  function renderProductImageCell(imageUrl) {
    return imageUrl
      ? `<img src="${escapeAttribute(imageUrl)}" alt="" loading="lazy" class="open-baskets-table__image">`
      : "-";
  }
  function loadingMarkup(label) {
    return `<span class="open-baskets-loading__spinner" aria-hidden="true"></span><span>${escapeHtml(label || "Loading…")}</span>`;
  }
  function setListLoading(node, label) {
    if (!node) return;
    node.innerHTML = `<p class="open-baskets-loading" role="status" aria-live="polite">${loadingMarkup(label)}</p>`;
  }
  function setTableLoading(node, colspan, label) {
    if (!node) return;
    node.innerHTML = `<tr><td colspan="${Number(colspan || 1)}" class="open-baskets-loading-cell"><div class="open-baskets-loading" role="status" aria-live="polite">${loadingMarkup(label)}</div></td></tr>`;
  }
  function startCountdown(node, deadline, label) {
    if (!node) return;
    if (node._openBasketsInterval) {
      clearInterval(node._openBasketsInterval);
      node._openBasketsInterval = null;
    }
    if (!deadline) {
      node.hidden = true;
      node.textContent = "";
      return;
    }
    const target = new Date(deadline).getTime();
    if (!Number.isFinite(target)) {
      node.hidden = true;
      node.textContent = "";
      return;
    }
    node.hidden = false;
    const paint = () => {
      const remainingMs = target - Date.now();
      if (remainingMs <= 0) {
        node.textContent = `${label}: 00:00:00`;
        return;
      }
      const total = Math.floor(remainingMs / 1000);
      const days = Math.floor(total / 86400);
      const hours = String(Math.floor((total % 86400) / 3600)).padStart(2, "0");
      const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
      const seconds = String(total % 60).padStart(2, "0");
      if (days > 0) {
        const dayLabel = days === 1 ? "day" : "days";
        node.textContent = `${label}: ${days} ${dayLabel} and ${hours}:${minutes}:${seconds}`;
      } else {
        node.textContent = `${label}: ${hours}:${minutes}:${seconds}`;
      }
    };
    paint();
    node._openBasketsInterval = setInterval(paint, 1000);
  }
  function initReserveWidgets() {
    const widgets = document.querySelectorAll("[data-open-baskets-reserve]");
    widgets.forEach((widget) => {
      const apiBase = normalizeApiBase(widget.dataset.apiBase || "/apps/open-baskets");
      const basketPageUrl = String(widget.dataset.basketPageUrl || "/pages/open-baskets").trim() || "/pages/open-baskets";
      const variantInputSelector = widget.dataset.variantInput || ".product-variant-id";
      const quantityInputSelector = widget.dataset.quantityInput || 'input[name="quantity"]';
      const variantAvailabilityRaw = String(widget.dataset.variantAvailability || "").trim();
      const modalId = widget.dataset.target;
      const modal = modalId ? document.getElementById(modalId) : null;
      const openButton = widget.querySelector("[data-open-baskets-open]");
      const closeButton = modal ? modal.querySelector("[data-open-baskets-close]") : null;
      const addNewButton = modal ? modal.querySelector("[data-open-baskets-new]") : null;
      const createButton = modal ? modal.querySelector("[data-open-baskets-create]") : null;
      const modalQuantityInput = modal ? modal.querySelector("[data-open-baskets-quantity]") : null;
      const basketList = modal ? modal.querySelector("[data-open-baskets-list]") : null;
      const nameWrap = modal ? modal.querySelector("[data-open-baskets-name-wrap]") : null;
      const nameInput = modal ? modal.querySelector("[data-open-baskets-name]") : null;
      const message = modal ? modal.querySelector("[data-open-baskets-message]") : null;
      const isAuthenticated = String(widget.dataset.customerAuth || "false") === "true";
      let reserveBlockedUnavailable = false;
      let variantAvailability = {};
      try {
        variantAvailability = variantAvailabilityRaw ? JSON.parse(variantAvailabilityRaw) : {};
      } catch {
        variantAvailability = {};
      }
      let latestBaskets = [];
      let latestSuggestedBasketName = "";
      const getProductRoot = () => widget.closest("product-form") || widget.closest(".product__info-container") || document;
      const normalizeVariantId = (value) => String(value || "").trim().replace(/^gid:\/\/shopify\/ProductVariant\//, "");
      const findVariantId = () => {
        const root = getProductRoot();
        const input = root.querySelector(variantInputSelector);
        return input ? String(input.value || "").trim() : "";
      };
      const findQuantity = () => {
        if (modalQuantityInput) {
          const modalQty = Number(modalQuantityInput.value || 1);
          if (Number.isFinite(modalQty) && modalQty > 0) return Math.floor(modalQty);
        }
        const root = getProductRoot();
        const input = root.querySelector(quantityInputSelector);
        const qty = Number(input && input.value ? input.value : 1);
        return Number.isFinite(qty) && qty > 0 ? qty : 1;
      };
      const updateReserveAvailability = () => {
        if (!openButton) return;
        const id = normalizeVariantId(findVariantId());
        if (!id || !(id in variantAvailability)) {
          reserveBlockedUnavailable = false;
          openButton.setAttribute("aria-disabled", "false");
          openButton.disabled = false;
          return;
        }
        const isAvailable = Boolean(variantAvailability[id]);
        reserveBlockedUnavailable = !isAvailable;
        openButton.setAttribute("aria-disabled", reserveBlockedUnavailable ? "true" : "false");
        openButton.disabled = reserveBlockedUnavailable;
      };
      const setButtonReserved = () => {
        const subtitle = widget.querySelector(".open-baskets-reserve__subtitle");
        if (subtitle) subtitle.textContent = "In a reserved basket";
      };
      const goToBasketPage = (basketId) => {
        const targetBasketId = String(basketId || "").trim();
        if (!targetBasketId) {
          window.location.assign(basketPageUrl);
          return;
        }
        const separator = basketPageUrl.includes("?") ? "&" : "?";
        window.location.assign(`${basketPageUrl}${separator}basketId=${encodeURIComponent(targetBasketId)}`);
      };
      const showCreateBasketForm = (baskets) => {
        if (nameWrap) nameWrap.hidden = false;
        if (addNewButton) addNewButton.hidden = true;
        if (nameInput) {
          nameInput.value = defaultBasketName(baskets, latestSuggestedBasketName);
          nameInput.focus();
          nameInput.select();
        }
      };
      const setReserveButtonLoading = (isLoading) => {
        if (!openButton) return;
        openButton.classList.toggle("loading", isLoading);
        openButton.disabled = isLoading || reserveBlockedUnavailable;
        openButton.setAttribute("aria-busy", isLoading ? "true" : "false");
        const spinner = openButton.querySelector(".loading__spinner");
        if (spinner) spinner.classList.toggle("hidden", !isLoading);
      };
      const addToBasket = async ({ basketId, basketName }) => {
        const variantId = findVariantId();
        if (!variantId) throw new Error("Please choose a product variant first.");
        const params = new URLSearchParams();
        if (basketId) params.set("basketId", String(basketId));
        if (basketName) params.set("basketName", String(basketName));
        params.set("variantId", String(variantId));
        params.set("quantity", String(findQuantity()));
        const payload = await fetchJson(`${apiBase}/baskets/items/add?${params.toString()}`);
        setButtonReserved();
        return payload;
      };
      const loadBaskets = async () => {
        if (!basketList) return [];
        showMessage(message, "", "info");
        basketList.innerHTML = '<p class="open-baskets-loading">Loading baskets…</p>';
        if (nameWrap) nameWrap.hidden = true;
        if (addNewButton) addNewButton.hidden = true;
        const payload = await fetchJson(`${apiBase}/baskets`);
        const baskets = Array.isArray(payload.baskets) ? payload.baskets : [];
        latestSuggestedBasketName = String(payload.suggestedBasketName || "").trim();
        latestBaskets = baskets;
        basketList.innerHTML = "";
        if (addNewButton) addNewButton.hidden = false;
        if (baskets.length === 0) {
          if (nameWrap) nameWrap.hidden = true;
          return [];
        }
        if (nameWrap) nameWrap.hidden = true;
        const sortedBaskets = [...baskets].sort((a, b) => {
          const nameA = String((a && a.name) || "Reserved basket");
          const nameB = String((b && b.name) || "Reserved basket");
          return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
        });
        sortedBaskets.forEach((basket) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "button button--secondary open-baskets-modal__basket-button";
          const basketName = basket.name || "Reserved basket";
          const nameSpan = document.createElement("span");
          nameSpan.className = "open-baskets-modal__basket-name";
          nameSpan.textContent = basketName;
          nameSpan.title = basketName;
          button.appendChild(nameSpan);
          button.insertAdjacentHTML("beforeend", BUTTON_SPINNER_MARKUP);
          button.addEventListener("click", async () => {
            await runButtonAction(button, async () => {
              const payload = await addToBasket({ basketId: basket.id });
              goToBasketPage(payload?.basketId || basket.id);
            }, (error) => {
              showMessage(message, getErrorMessage(error), "error");
            }, "open-baskets-modal__button--loading");
          });
          basketList.appendChild(button);
        });
        return baskets;
      };
      if (!openButton || !modal) return;
      updateReserveAvailability();
      const rootForVariant = getProductRoot();
      if (rootForVariant && typeof rootForVariant.addEventListener === "function") {
        rootForVariant.addEventListener("change", (event) => {
          const target = event.target;
          if (!(target instanceof Element)) return;
          if (target.matches(variantInputSelector)) updateReserveAvailability();
        });
      }
      openButton.addEventListener("click", async (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (modalQuantityInput) modalQuantityInput.value = "1";
        updateReserveAvailability();
        if (reserveBlockedUnavailable) return;
        if (!isAuthenticated) {
          modal.showModal();
          return;
        }
        setReserveButtonLoading(true);
        try {
          await loadBaskets();
          modal.showModal();
        } catch (error) {
          modal.showModal();
          if (addNewButton) addNewButton.hidden = false;
          showMessage(message, getErrorMessage(error), "error");
        } finally {
          setReserveButtonLoading(false);
        }
      });
      if (closeButton) closeButton.addEventListener("click", () => modal.close());
      if (addNewButton && nameWrap) {
        addNewButton.addEventListener("click", () => {
          addNewButton.hidden = true;
          showCreateBasketForm(latestBaskets);
        });
      }
      if (nameInput) {
        nameInput.addEventListener("keydown", (event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          event.stopPropagation();
          if (createButton) createButton.click();
        });
      }
      if (createButton) {
        createButton.addEventListener("click", async () => {
          const basketName = nameInput ? String(nameInput.value || "").trim() : "";
          await runButtonAction(createButton, async () => {
          const payload = await addToBasket({ basketName });
          goToBasketPage(payload?.basketId || "");
        }, (error) => {
          if (showDuplicateBasketNamePopup(error)) return;
          showMessage(message, getErrorMessage(error), "error");
        }, "open-baskets-modal__button--loading");
      });
      }
    });
  }
  function initDashboard() {
    const root = document.querySelector("[data-open-baskets-dashboard]");
    if (!root) return;
    const apiBase = normalizeApiBase(root.dataset.apiBase || "/apps/open-baskets");
    const customerAuth = String(root.dataset.customerAuth || "false") === "true";
    if (!customerAuth) return;
    const basketList = root.querySelector("[data-dashboard-baskets]");
    const tableBody = root.querySelector("[data-dashboard-items]");
    const message = root.querySelector("[data-dashboard-message]");
    const graceNode = root.querySelector("[data-dashboard-grace]");
    const paymentNode = root.querySelector("[data-dashboard-payment]");
    const pendingPaymentNode = root.querySelector("[data-dashboard-pending-payment]");
    const moveButton = root.querySelector("[data-dashboard-move]");
    const removeButton = root.querySelector("[data-dashboard-remove]");
    const moveModal = root.querySelector("[data-dashboard-move-modal]");
    const removeModal = root.querySelector("[data-dashboard-remove-modal]");
    const removeWarningModal = root.querySelector("[data-dashboard-remove-warning-modal]");
    const removeWarningCloseButton = root.querySelector("[data-dashboard-remove-warning-close]");
    const removeConfirmButton = root.querySelector("[data-dashboard-remove-confirm]");
    const removeQuantityList = root.querySelector("[data-dashboard-remove-list]");
    const moveList = root.querySelector("[data-dashboard-move-list]");
    const moveNewToggleButton = root.querySelector("[data-dashboard-move-new-toggle]");
    const moveNewWrap = root.querySelector("[data-dashboard-move-new-wrap]");
    const moveNewNameInput = root.querySelector("[data-dashboard-move-new-name]");
    const moveNewConfirmButton = root.querySelector("[data-dashboard-move-new-confirm]");
    const totalNode = root.querySelector("[data-dashboard-total]");
    const totalDiscountNode = root.querySelector("[data-dashboard-total-discount]");
    const totalDiscountCodesNode = root.querySelector("[data-dashboard-total-discount-codes]");
    const totalShippingMainNode = root.querySelector("[data-dashboard-total-shipping-main]");
    const totalShippingDetailNode = root.querySelector("[data-dashboard-total-shipping-detail]");
    const pendingTotalNode = root.querySelector("[data-dashboard-pending-total]");
    const pendingTotalDiscountNode = root.querySelector("[data-dashboard-pending-total-discount]");
    const pendingTotalDiscountCodesNode = root.querySelector("[data-dashboard-pending-total-discount-codes]");
    const finalizeButton = root.querySelector("[data-dashboard-finalize]");
    const cancelButton = root.querySelector("[data-dashboard-cancel]");
    const finalizeModal = root.querySelector("[data-dashboard-finalize-modal]");
    const cancelModal = root.querySelector("[data-dashboard-cancel-modal]");
    const finalizeConfirmButton = root.querySelector("[data-dashboard-finalize-confirm]");
    const cancelConfirmButton = root.querySelector("[data-dashboard-cancel-confirm]");
    const modalCloseButtons = root.querySelectorAll("[data-dashboard-modal-close]");
    const tabButtons = Array.from(root.querySelectorAll("[data-dashboard-tab]"));
    const tabPanels = Array.from(root.querySelectorAll("[data-dashboard-panel]"));
    const tabDescription = root.querySelector("[data-dashboard-tab-description]");
    const pendingList = root.querySelector("[data-dashboard-pending-list]");
    const pendingItems = root.querySelector("[data-dashboard-pending-items]");
    const completedList = root.querySelector("[data-dashboard-completed-list]");
    const completedItems = root.querySelector("[data-dashboard-completed-items]");
    const completedOrderLink = root.querySelector("[data-dashboard-completed-order-link]");
    const cancelledList = root.querySelector("[data-dashboard-cancelled-list]");
    const cancelledItems = root.querySelector("[data-dashboard-cancelled-items]");
    const cancelledStatus = root.querySelector("[data-dashboard-cancelled-status]");
    const resendPaymentButton = root.querySelector("[data-dashboard-resend-payment]");
    const pendingCancelButton = root.querySelector("[data-dashboard-pending-cancel]");
    const pendingCancelModal = root.querySelector("[data-dashboard-pending-cancel-modal]");
    const pendingCancelConfirmButton = root.querySelector("[data-dashboard-pending-cancel-confirm]");
    const sortableHeaders = Array.from(root.querySelectorAll(".open-baskets-table th[data-sort-key]"));
    let selectedBasketId = null;
    let selectedBasketDraftOrderId = null;
    let preferredDraftBasketId = (() => {
      try {
        const search = new URLSearchParams(window.location.search || "");
        return String(search.get("basketId") || "").trim();
      } catch {
        return "";
      }
    })();
    let selectedItemIds = [];
    let pendingRemoveItemIds = [];
    let latestMoveTargets = [];
    let latestOpenBaskets = [];
    let latestSuggestedBasketName = "";
    let currentDetails = null;
    let sortState = { key: "product", direction: "asc" };
    const archiveSortState = {
      pending: { key: "product", direction: "asc" },
      completed: { key: "product", direction: "asc" },
    };
    let tabsData = null;
    let tabsLoaded = false;
    let tabsLoadPromise = null;
    let activeTab = "open";
    let detailRequestSeq = 0;
    const selectedArchiveBasket = { pending: "", completed: "", cancelled: "" };
    const archiveDetails = { pending: null, completed: null, cancelled: null };
    const getBasketRouteId = (basket) => String((basket && (basket.dbId || basket.id)) || "");
    const getBasketDraftOrderId = (basket) => String((basket && (basket.draftOrderId || basket.id)) || "");
    const setShippingMain = (value) => {
      if (!totalShippingMainNode) return;
      totalShippingMainNode.innerHTML = "";
      const label = document.createElement("strong");
      label.textContent = "Shipping:";
      totalShippingMainNode.appendChild(label);
      if (value) totalShippingMainNode.appendChild(document.createTextNode(` ${value}`));
    };
    const getTabRows = (tabName) => {
      if (!tabsData) return [];
      if (tabName === "pending") return Array.isArray(tabsData.paymentPending) ? tabsData.paymentPending : [];
      if (tabName === "completed") return Array.isArray(tabsData.completed) ? tabsData.completed : [];
      if (tabName === "cancelled") return Array.isArray(tabsData.cancelledUnpaid) ? tabsData.cancelledUnpaid : [];
      return [];
    };
    const getTabListNode = (tabName) => {
      if (tabName === "pending") return pendingList;
      if (tabName === "completed") return completedList;
      if (tabName === "cancelled") return cancelledList;
      return null;
    };
    const getTabItemsNode = (tabName) => {
      if (tabName === "pending") return pendingItems;
      if (tabName === "completed") return completedItems;
      if (tabName === "cancelled") return cancelledItems;
      return null;
    };
    const getCancelledStatusLabel = (row) => {
      const fromHistory = String(row?.historyStatus || "").trim();
      if (fromHistory === "Cancelled" || fromHistory === "Unpaid" || fromHistory === "Paid") return fromHistory;
      const raw = String(row?.reservationStatus || "").trim().toLowerCase();
      if (raw === "paid") return "Paid";
      if (raw === "cancelled" || raw === "expired") return "Cancelled";
      if (row?.firstInvoiceSentAt) return "Unpaid";
      return "Cancelled";
    };
    const getTabDescription = (tabName) => {
      if (tabName === "pending") {
        return "These baskets have been finalized and a payment link has been sent.\nComplete payment before the deadline or resend the payment link if needed.";
      }
      if (tabName === "completed") {
        return "These baskets have been paid and are kept here for reference.\nYou can still check item status and expected in-store dates for availability information.";
      }
      if (tabName === "cancelled") {
        return "These baskets were cancelled or expired before payment was completed.\nReview the details here, but cancelled baskets can no longer be edited or paid.";
      }
      return "Your items are reserved and ordered from the distributor even before finalizing the basket.\nFor pre-orders it is best to keep baskets open as release dates may change and you might want to move items to a new basket.";
    };
    const getCancelledStatusTone = (label) => {
      const status = String(label || "").toLowerCase();
      if (status === "paid") return "success";
      if (status === "unpaid") return "warning";
      return "critical";
    };
    const renderCancelledDetailsStatus = (row, details) => {
      if (!cancelledStatus) return;
      const label = String(details?.basket?.historyStatus || "").trim() || getCancelledStatusLabel(row);
      if (!label) {
        cancelledStatus.textContent = "";
        return;
      }
      const tone = getCancelledStatusTone(label);
      cancelledStatus.innerHTML = `Status: <span class="open-baskets-admin-status-badge open-baskets-admin-status-badge--${tone}">${escapeHtml(label)}</span>`;
    };
    const setCompletedOrderLink = (row) => {
      if (!completedOrderLink) return;
      if (!row || !row.orderUrl) {
        completedOrderLink.textContent = "";
        return;
      }
      const orderName = row.orderName || "View order";
      completedOrderLink.innerHTML = `Order: <a href="${escapeAttribute(row.orderUrl)}" target="_blank" rel="noopener">${escapeHtml(orderName)}</a>`;
    };
    const renderPricingSummary = (details, nodes, fallbackItems = []) => {
      const totalTarget = nodes?.total || null;
      const discountTarget = nodes?.discount || null;
      const codesTarget = nodes?.codes || null;
      const pricing = details && typeof details === "object" ? details.pricing : null;
      const items = Array.isArray(fallbackItems) ? fallbackItems : [];
      const pricingTotal = pricing && pricing.totalPrice !== null && pricing.totalPrice !== undefined
        ? Number(pricing.totalPrice)
        : NaN;
      const fallbackTotal = items.reduce((sum, item) => {
        const lineTotal = item.lineTotal !== undefined && item.lineTotal !== null
          ? Number(item.lineTotal || 0)
          : Number(item.lockedUnitPrice || 0) * Number(item.quantity || 0);
        return sum + (Number.isFinite(lineTotal) ? lineTotal : 0);
      }, 0);
      const total = Number.isFinite(pricingTotal) ? pricingTotal : fallbackTotal;
      const currency = String(pricing?.currencyCode || items[0]?.currencyCode || details?.currencyCode || "EUR");
      if (totalTarget) totalTarget.textContent = items.length > 0 || Number.isFinite(pricingTotal) ? formatMoney(total, currency) : "-";

      const discountValue = pricing && pricing.totalDiscounts !== null && pricing.totalDiscounts !== undefined
        ? Number(pricing.totalDiscounts)
        : 0;
      if (discountTarget) {
        if (Number.isFinite(discountValue) && discountValue > 0) {
          discountTarget.hidden = false;
          discountTarget.textContent = `Discount: -${formatMoney(discountValue, currency)}`;
        } else {
          discountTarget.hidden = true;
          discountTarget.textContent = "";
        }
      }

      if (codesTarget) {
        const codes = Array.isArray(pricing?.discountCodes) ? pricing.discountCodes.filter(Boolean) : [];
        if (codes.length > 0) {
          codesTarget.hidden = false;
          codesTarget.textContent = `${codes.length === 1 ? "Code" : "Codes"}: ${codes.join(", ")}`;
        } else {
          codesTarget.hidden = true;
          codesTarget.textContent = "";
        }
      }
    };
    const renderArchiveItems = (tabName, details) => {
      const itemsNode = getTabItemsNode(tabName);
      if (!itemsNode) return;
      let items = Array.isArray(details?.items) ? details.items : [];
      const isOpenStyleArchive = tabName === "pending" || tabName === "completed";
      if (tabName === "pending") {
        renderPricingSummary(details, {
          total: pendingTotalNode,
          discount: pendingTotalDiscountNode,
          codes: pendingTotalDiscountCodesNode,
        }, items);
      }
      if (isOpenStyleArchive) {
        items = sortItems(items, archiveSortState[tabName]);
      }
      if (items.length === 0) {
        itemsNode.innerHTML = `<tr><td colspan="${isOpenStyleArchive ? 7 : 4}" class="open-baskets-empty">No products in this basket.</td></tr>`;
        if (tabName === "pending") {
          renderPricingSummary({ pricing: null }, {
            total: pendingTotalNode,
            discount: pendingTotalDiscountNode,
            codes: pendingTotalDiscountCodesNode,
          }, []);
        }
        if (tabName === "completed") setCompletedOrderLink(getTabRows("completed").find((row) => String(row.draftOrderId) === String(selectedArchiveBasket.completed)));
        return;
      }
      itemsNode.innerHTML = "";
      items.forEach((item) => {
        const row = document.createElement("tr");
        const imageCell = renderProductImageCell(item.imageUrl);
        const productCell = renderProductSummaryCell(item);
        if (isOpenStyleArchive) {
          const statusLabel = item.statusLabel || item.status || "Unknown";
          const statusText = escapeHtml(statusLabel);
          const expected = computeExpectedInStore(item);
          const expectedText = expected ? formatDateOnly(expected) : "-";
          const qtyText = String(Number(item.quantity || 0));
          const priceText = formatMoney(item.lockedUnitPrice || 0, item.currencyCode || "EUR");
          row.className = "open-baskets-table__row open-baskets-table__row--static";
          row.innerHTML = `
            <td>${imageCell}</td>
            <td>${productCell}</td>
            <td>${item.releaseDate ? formatDateOnly(item.releaseDate) : "-"}</td>
            <td><span class="open-basket-status ${statusClass(statusLabel)}">${statusText}</span></td>
            <td>${expectedText}</td>
            <td>${qtyText}</td>
            <td>${priceText}</td>
          `;
          const mobileMetaRow = document.createElement("tr");
          mobileMetaRow.className = "open-baskets-table__mobile-align-row";
          mobileMetaRow.innerHTML = `
            <td></td>
            <td><span class="caption">Expected: ${expectedText}</span></td>
            <td><span class="caption">Qty: ${qtyText}</span></td>
            <td><span class="caption">Price: ${priceText}</span></td>
            <td></td>
            <td></td>
            <td></td>
          `;
          itemsNode.appendChild(row);
          itemsNode.appendChild(mobileMetaRow);
        } else {
          const qtyText = String(Number(item.quantity || 0));
          const priceText = formatMoney(item.lockedUnitPrice || 0, item.currencyCode || "EUR");
          row.className = "open-baskets-table__row open-baskets-table__row--static";
          row.innerHTML = `
            <td>${imageCell}</td>
            <td>${productCell}</td>
            <td>${qtyText}</td>
            <td>${priceText}</td>
          `;
          const mobileMetaRow = document.createElement("tr");
          mobileMetaRow.className = "open-baskets-table__mobile-align-row";
          mobileMetaRow.innerHTML = `
            <td></td>
            <td colspan="3"><span class="caption">Qty: ${qtyText}</span><span class="caption open-baskets-table__mobile-meta-separator">Price: ${priceText}</span></td>
          `;
          itemsNode.appendChild(row);
          itemsNode.appendChild(mobileMetaRow);
        }
      });
      if (tabName === "completed") {
        const row = getTabRows("completed").find((item) => String(item.id) === String(selectedArchiveBasket.completed));
        setCompletedOrderLink(row || null);
      }
    };
    const loadArchiveDetails = async (tabName) => {
      const selectedBasketDbId = selectedArchiveBasket[tabName];
      const itemsNode = getTabItemsNode(tabName);
      const selectedRow = getTabRows(tabName).find((row) => String(row.id) === String(selectedBasketDbId)) || null;
      const draftOrderId = selectedRow?.draftOrderId ? String(selectedRow.draftOrderId) : "";
      const orderId = selectedRow?.orderId ? String(selectedRow.orderId) : "";
      const basketId = selectedRow?.id ? String(selectedRow.id) : "";
      if (!itemsNode) return;
      if (!draftOrderId && !orderId) {
        const isOpenStyleArchive = tabName === "pending" || tabName === "completed";
        itemsNode.innerHTML = `<tr><td colspan="${isOpenStyleArchive ? 7 : 4}" class="open-baskets-empty">Select a basket to view products.</td></tr>`;
        if (tabName === "pending") {
          renderPricingSummary({ pricing: null }, {
            total: pendingTotalNode,
            discount: pendingTotalDiscountNode,
            codes: pendingTotalDiscountCodesNode,
          }, []);
        }
        if (tabName === "completed") setCompletedOrderLink(null);
        if (tabName === "cancelled") renderCancelledDetailsStatus(null, null);
        if (tabName !== "open") {
          startCountdown(graceNode, null, "");
          startCountdown(paymentNode, null, "");
          startCountdown(pendingPaymentNode, null, "");
        }
        return;
      }
      setTableLoading(itemsNode, tabName === "cancelled" ? 4 : 7, "Loading basket products…");
      if (tabName === "completed") setCompletedOrderLink(null);
      if (tabName === "cancelled") renderCancelledDetailsStatus(null, null);
      const params = new URLSearchParams({
        draftOrderId,
        basketId,
        orderId,
        tab: tabName,
      });
      const details = await fetchJson(`${apiBase}/baskets/tabs-detail?${params.toString()}`);
      archiveDetails[tabName] = details;
      renderArchiveItems(tabName, details);
      if (tabName === "cancelled") renderCancelledDetailsStatus(selectedRow, details);
      if (tabName === "pending") {
        startCountdown(graceNode, null, "");
        startCountdown(paymentNode, details?.paymentDueAt || null, "Pay in");
        startCountdown(pendingPaymentNode, details?.paymentDueAt || null, "Pay in");
      } else {
        startCountdown(graceNode, null, "");
        startCountdown(paymentNode, null, "");
        startCountdown(pendingPaymentNode, null, "");
      }
    };
    const renderArchiveList = (tabName) => {
      const listNode = getTabListNode(tabName);
      if (!listNode) return;
      const rows = getTabRows(tabName);
      listNode.innerHTML = "";
      if (rows.length === 0) {
        listNode.innerHTML = '<p class="open-baskets-empty">No baskets found.</p>';
        selectedArchiveBasket[tabName] = "";
        if (tabName === "pending" && resendPaymentButton) resendPaymentButton.disabled = true;
        void loadArchiveDetails(tabName);
        return;
      }
      if (!selectedArchiveBasket[tabName] || !rows.some((row) => String(row.id) === String(selectedArchiveBasket[tabName]))) {
        selectedArchiveBasket[tabName] = String(rows[0].id || "");
      }
      rows.forEach((row) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "button button--secondary open-baskets-list__basket-btn";
        if (String(row.id) === String(selectedArchiveBasket[tabName])) button.classList.add("is-active");
        const nameSpan = document.createElement("span");
        nameSpan.className = "open-baskets-list__basket-name";
        nameSpan.textContent = row.name || "Reserved basket";
        nameSpan.title = row.name || "Reserved basket";
        button.appendChild(nameSpan);
        button.addEventListener("click", () => {
          selectedArchiveBasket[tabName] = String(row.id || "");
          renderArchiveList(tabName);
          void loadArchiveDetails(tabName).catch((error) => showMessage(message, getErrorMessage(error), "error"));
        });
        listNode.appendChild(button);
      });
      if (tabName === "pending" && resendPaymentButton) resendPaymentButton.disabled = !selectedArchiveBasket.pending;
      if (tabName === "pending" && pendingCancelButton) pendingCancelButton.disabled = !selectedArchiveBasket.pending;
      if (activeTab === tabName) {
        void loadArchiveDetails(tabName);
      }
    };
    const markArchiveListsLoading = () => {
      setListLoading(pendingList, "Loading baskets…");
      setListLoading(completedList, "Loading baskets…");
      setListLoading(cancelledList, "Loading baskets…");
      setTableLoading(pendingItems, 7, "Loading basket products…");
      setTableLoading(completedItems, 7, "Loading basket products…");
      setTableLoading(cancelledItems, 4, "Loading basket products…");
    };
    const invalidateTabsData = () => {
      tabsData = null;
      tabsLoaded = false;
      tabsLoadPromise = null;
      archiveDetails.pending = null;
      archiveDetails.completed = null;
      archiveDetails.cancelled = null;
    };
    const loadTabsData = async ({ force = false } = {}) => {
      if (tabsLoaded && !force) {
        renderArchiveList("pending");
        renderArchiveList("completed");
        renderArchiveList("cancelled");
        return tabsData;
      }
      if (tabsLoadPromise && !force) {
        return tabsLoadPromise;
      }
      markArchiveListsLoading();
      tabsLoadPromise = fetchJson(`${apiBase}/baskets/tabs`)
        .then((payload) => {
          tabsData = payload;
          tabsLoaded = true;
          renderArchiveList("pending");
          renderArchiveList("completed");
          renderArchiveList("cancelled");
          return tabsData;
        })
        .finally(() => {
          tabsLoadPromise = null;
        });
      return tabsLoadPromise;
    };
    const switchTab = async (tabName) => {
      activeTab = tabName;
      if (tabDescription) {
        tabDescription.innerHTML = escapeHtml(getTabDescription(tabName)).replace(/\n/g, "<br>");
        tabDescription.classList.add("open-baskets-dashboard-warning");
      }
      tabButtons.forEach((button) => {
        const isActive = String(button.getAttribute("data-dashboard-tab") || "") === tabName;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-selected", isActive ? "true" : "false");
      });
      tabPanels.forEach((panel) => {
        panel.hidden = String(panel.getAttribute("data-dashboard-panel") || "") !== tabName;
      });
      if (tabName === "open") {
        startCountdown(graceNode, null, "");
        startCountdown(paymentNode, null, "");
        startCountdown(pendingPaymentNode, null, "");
        await refreshBaskets();
        updateSortHeaders();
        return;
      }
      startCountdown(graceNode, null, "");
      startCountdown(paymentNode, null, "");
      await loadTabsData();
      updateSortHeaders();
    };
    const updateSelection = () => {
      selectedItemIds = Array.from(root.querySelectorAll("tr[data-item-id].is-selected")).map((row) =>
        String(row.getAttribute("data-item-id") || ""),
      ).filter(Boolean);
      const hasSelection = selectedItemIds.length > 0;
      if (moveButton) moveButton.disabled = !hasSelection;
      if (removeButton) removeButton.disabled = !hasSelection;
    };
    const formatDateOnly = (value) => {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return new Intl.DateTimeFormat(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
    };
    const parseDate = (value) => {
      if (!value) return 0;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    };
    const computeExpectedInStore = (item) => (item?.expectedInStore ? String(item.expectedInStore) : null);
    const statusLoadingCell = () => (
      '<span class="open-baskets-status-loading" role="status" aria-label="Loading status"><span class="open-baskets-loading__spinner" aria-hidden="true"></span></span>'
    );
    const getSortValue = (item, sortKey) => {
      switch (sortKey) {
        case "product":
          return String(item?.title || "").toLowerCase();
        case "releaseDate":
          return parseDate(item?.releaseDate);
        case "status":
          return String(item?.statusLabel || item?.status || "").toLowerCase();
        case "quantity":
          return Number(item?.quantity || 0);
        case "price":
          return Number(item?.lockedUnitPrice || 0);
        case "expectInStore":
          return parseDate(computeExpectedInStore(item));
        default:
          return "";
      }
    };
    const sortItems = (items, localSortState = sortState) => {
      const direction = localSortState.direction === "desc" ? -1 : 1;
      const key = localSortState.key;
      return [...items].sort((a, b) => {
        const aValue = getSortValue(a, key);
        const bValue = getSortValue(b, key);
        if (typeof aValue === "number" && typeof bValue === "number") {
          return (aValue - bValue) * direction;
        }
        return String(aValue).localeCompare(String(bValue), undefined, { sensitivity: "base" }) * direction;
      });
    };
    const updateSortHeaders = () => {
      sortableHeaders.forEach((header) => {
        const panel = header.closest("[data-dashboard-panel]");
        const tabName = String(panel?.getAttribute("data-dashboard-panel") || "open");
        const key = String(header.getAttribute("data-sort-key") || "");
        const state = tabName === "open"
          ? sortState
          : tabName === "pending" || tabName === "completed"
            ? archiveSortState[tabName]
            : null;
        const isActive = tabName === activeTab && !!state && key === state.key;
        header.setAttribute("role", "button");
        header.setAttribute("tabindex", "0");
        header.setAttribute("aria-sort", isActive ? (state.direction === "asc" ? "ascending" : "descending") : "none");
      });
    };
    const applySort = (key, tabName = activeTab) => {
      if (!key) return;
      if (tabName === "open") {
        if (sortState.key === key) {
          sortState = {
            key,
            direction: sortState.direction === "asc" ? "desc" : "asc",
          };
        } else {
          sortState = { key, direction: "asc" };
        }
        if (currentDetails) renderItems(currentDetails);
      } else if (tabName === "pending" || tabName === "completed") {
        const currentState = archiveSortState[tabName];
        if (currentState.key === key) {
          archiveSortState[tabName] = {
            key,
            direction: currentState.direction === "asc" ? "desc" : "asc",
          };
        } else {
          archiveSortState[tabName] = { key, direction: "asc" };
        }
        const details = archiveDetails[tabName];
        if (details) renderArchiveItems(tabName, details);
      } else {
        return;
      }
      updateSortHeaders();
    };
    const PENCIL_SVG = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>`;
    const renderBaskets = (baskets) => {
      if (!basketList) return;
      basketList.innerHTML = "";
      const sorted = [...baskets].sort((a, b) => {
        const nameA = String((a && a.name) || "Reserved basket");
        const nameB = String((b && b.name) || "Reserved basket");
        return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
      });
      sorted.forEach((basket) => {
        const basketRouteId = getBasketRouteId(basket);
        const basketDraftOrderId = getBasketDraftOrderId(basket);

        // ── basket select button (pencil is inside this button) ──────────────
        const button = document.createElement("button");
        button.type = "button";
        button.className = "button button--secondary open-baskets-list__basket-btn";
        if (selectedBasketId && basketRouteId === String(selectedBasketId)) button.classList.add("is-active");

        const nameSpan = document.createElement("span");
        nameSpan.className = "open-baskets-list__basket-name";
        nameSpan.textContent = basket.name || "Reserved basket";
        nameSpan.title = basket.name || "Reserved basket";
        button.appendChild(nameSpan);

        // ── pencil rename trigger (inside the button) ─────────────────────────
        const renameBtn = document.createElement("span");
        renameBtn.className = "open-baskets-list__rename-btn";
        renameBtn.setAttribute("role", "button");
        renameBtn.setAttribute("aria-label", "Rename basket");
        renameBtn.setAttribute("tabindex", "0");
        renameBtn.innerHTML = PENCIL_SVG;
        button.appendChild(renameBtn);

        button.addEventListener("click", (e) => {
          // Ignore clicks while editing the name or on rename controls
          if (button.classList.contains("is-renaming")) return;
          if (e.target instanceof Element && (
            e.target.closest(".open-baskets-list__rename-btn") ||
            e.target.closest(".open-baskets-list__rename-input")
          )) return;
          selectedBasketId = basketRouteId;
          selectedBasketDraftOrderId = basketDraftOrderId;
          void refreshDetails();
        });

        renameBtn.addEventListener("keydown", (e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          renameBtn.click();
        });

        renameBtn.addEventListener("click", (e) => {
          e.stopPropagation();

          if (button.classList.contains("is-renaming")) return;
          // Hide current label while editing so the input uses the full row width
          const currentName = basket.name || "Reserved basket";
          button.classList.add("is-renaming");
          nameSpan.hidden = true;

          const input = document.createElement("input");
          input.type = "text";
          input.className = "open-baskets-list__rename-input";
          input.value = currentName;
          input.maxLength = 80;
          input.setAttribute("aria-label", "Basket name");

          button.insertBefore(input, renameBtn);
          input.focus();
          input.select();

          let outsideListenerAttached = false;
          const detachOutsideListener = () => {
            if (!outsideListenerAttached) return;
            document.removeEventListener("mousedown", onClickOutside, true);
            outsideListenerAttached = false;
          };

          const cancelRename = () => {
            detachOutsideListener();
            input.remove();
            button.classList.remove("is-renaming");
            nameSpan.hidden = false;
          };

          const confirmRename = async () => {
            detachOutsideListener();
            const newName = input.value.trim();
            input.remove();
            button.classList.remove("is-renaming");
            nameSpan.hidden = false;
            if (!newName || newName === currentName) return;
            try {
              await fetchJson(`${apiBase}/baskets/rename`, {
                method: "POST",
                headers: jsonHeaders,
                body: JSON.stringify({ basketId: basketDraftOrderId, basketName: newName }),
              });
              basket.name = newName;
              nameSpan.textContent = newName;
              await refreshBaskets();
            } catch (error) {
              showMessage(message, getErrorMessage(error), "error");
            }
          };

          input.addEventListener("keydown", (ev) => {
            if (ev.key === "Enter") { ev.preventDefault(); void confirmRename(); }
            if (ev.key === "Escape") { ev.preventDefault(); cancelRename(); }
          });

          const onClickOutside = (ev) => {
            if (!button.contains(ev.target)) {
              cancelRename();
            }
          };
          setTimeout(() => {
            document.addEventListener("mousedown", onClickOutside, true);
            outsideListenerAttached = true;
          }, 0);
        });

        basketList.appendChild(button);
      });
    };
    const renderShipping = (shipping) => {
      if (!totalShippingMainNode || !totalShippingDetailNode) return;
      if (!shipping) {
        setShippingMain("-");
        totalShippingDetailNode.textContent = "Add shipping address in your Profile to calculate.";
        return;
      }

      const fee = shipping.formattedFee || formatMoney(shipping.fee, shipping.currencyCode);
      const toFree = shipping.formattedAmountToFree || formatMoney(shipping.amountToFree, shipping.currencyCode);

      if (shipping.isFree) {
        setShippingMain("Free");
        totalShippingDetailNode.textContent = "";
      } else if (shipping.canCalculate) {
        setShippingMain(fee);
        totalShippingDetailNode.textContent = `Add ${toFree} for free shipping.`;
      } else {
        setShippingMain("-");
        totalShippingDetailNode.textContent = "Add shipping address in your Profile to calculate.";
      }
    };
    const statusClass = (status) => {
      const value = String(status || "").toLowerCase();
      if (value.includes("on hand") || value.includes("in stock") || value.includes("fulfilled")) return "open-basket-status--on-hand";
      if (value.includes("pre")) return "open-basket-status--pre-order";
      return "open-basket-status--backorder";
    };
    const renderItems = (details) => {
      if (!tableBody) return;
      currentDetails = details;
      tableBody.innerHTML = "";
      selectedItemIds = [];
      const items = sortItems(Array.isArray(details.items) ? details.items : []);
      if (items.length === 0) {
        const row = document.createElement("tr");
        row.innerHTML = '<td colspan="7" class="open-baskets-empty">No items in this basket.</td>';
        tableBody.appendChild(row);
        renderPricingSummary({ pricing: null }, {
          total: totalNode,
          discount: totalDiscountNode,
          codes: totalDiscountCodesNode,
        }, []);
        if (totalShippingMainNode) totalShippingMainNode.textContent = "";
        if (totalShippingDetailNode) totalShippingDetailNode.textContent = "";
        updateSelection();
        updateSortHeaders();
        return;
      }
      renderPricingSummary(details, {
        total: totalNode,
        discount: totalDiscountNode,
        codes: totalDiscountCodesNode,
      }, items);
      items.forEach((item) => {
        const row = document.createElement("tr");
        row.setAttribute("data-item-id", String(item.id || ""));
        row.className = "open-baskets-table__row";
        const statusLabel = item.statusLabel || item.status || "Unknown";
        const escapedStatusLabel = escapeHtml(statusLabel);
        const expectedInStore = computeExpectedInStore(item);
        const imageCell = renderProductImageCell(item.imageUrl);
        const statusLoading = Boolean(item.statusLoading);
        const statusCell = statusLoading
          ? statusLoadingCell()
          : `<span class="open-basket-status ${statusClass(statusLabel)}">${escapedStatusLabel}</span>`;
        const expectedText = statusLoading ? "" : (expectedInStore ? formatDateOnly(expectedInStore) : "-");
        const expectedCell = statusLoading ? statusLoadingCell() : expectedText;
        const qtyText = String(item.quantity || 0);
        const priceText = formatMoney(item.lockedUnitPrice, item.currencyCode);
        const productCell = renderProductSummaryCell(item);
         row.innerHTML = `
          <td>${imageCell}</td>
          <td>${productCell}</td>
          <td>${item.releaseDate ? formatDateOnly(item.releaseDate) : "-"}</td>
          <td>${statusCell}</td>
          <td>${expectedCell}</td>
          <td>${item.quantity || 0}</td>
          <td>${formatMoney(item.lockedUnitPrice, item.currencyCode)}</td>
        `;
        const mobileMetaRow = document.createElement("tr");
        mobileMetaRow.className = "open-baskets-table__mobile-align-row";
        mobileMetaRow.innerHTML = `
          <td></td>
          <td><span class="caption">Expected: ${statusLoading ? statusLoadingCell() : expectedText}</span></td>
          <td><span class="caption">Qty: ${qtyText}</span></td>
          <td><span class="caption">Price: ${priceText}</span></td>
          <td></td>
          <td></td>
          <td></td>
        `;
        const toggleSelected = () => {
          row.classList.toggle("is-selected");
          mobileMetaRow.classList.toggle("is-selected", row.classList.contains("is-selected"));
          updateSelection();
        };
        row.addEventListener("click", (event) => {
          const target = event.target;
          if (target instanceof Element && target.closest("a")) return;
          toggleSelected();
        });
        mobileMetaRow.addEventListener("click", () => {
          toggleSelected();
        });
        tableBody.appendChild(row);
        tableBody.appendChild(mobileMetaRow);
      });
      updateSelection();
      updateSortHeaders();
    };
    const executeMove = async ({ targetBasketId = null, targetBasketName = null }) => {
      updateSelection();
      if (!selectedBasketDraftOrderId) throw new Error("Please select a basket first.");
      if (selectedItemIds.length === 0) throw new Error("Please select at least one product to move.");
      const sourceDraftOrderId = String(selectedBasketDraftOrderId || "");
      const sourceSelectedIds = [...selectedItemIds];
      try {
        await fetchJson(`${apiBase}/baskets/items/move`, {
          method: "POST",
          headers: jsonHeaders,
          body: JSON.stringify({
            basketId: selectedBasketDraftOrderId,
            itemIds: selectedItemIds,
            targetBasketId,
            targetBasketName,
          }),
        });
      } catch (error) {
        const messageText = getErrorMessage(error, String(error));
        if (/temporarily unavailable/i.test(messageText)) {
          await refreshBaskets();
          if (sourceDraftOrderId && String(selectedBasketDraftOrderId || "") !== sourceDraftOrderId) {
            // Source basket was auto-archived/removed after moving last items.
            return;
          }
          const currentItemIds = new Set((Array.isArray(currentDetails?.items) ? currentDetails.items : []).map((item) => String(item?.id || "")));
          const stillInSource = sourceSelectedIds.some((id) => currentItemIds.has(String(id || "")));
          if (!stillInSource) return;
        }
        throw error;
      }
    };
    const renderMoveTargets = (details) => {
      if (!moveList) return;
      moveList.innerHTML = "";
      const targets = Array.isArray(details.moveTargets) ? details.moveTargets : [];
      latestMoveTargets = targets;
      const sortedTargets = [...targets].sort((a, b) => {
        const nameA = String((a && a.name) || "Reserved basket");
        const nameB = String((b && b.name) || "Reserved basket");
        return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
      });
      sortedTargets
        .filter((target) => String(target.id) !== String(selectedBasketId))
        .forEach((target) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "button button--secondary open-baskets-modal__basket-button";
          const basketName = target.name || `Basket ${target.id}`;
          const nameSpan = document.createElement("span");
          nameSpan.className = "open-baskets-modal__basket-name";
          nameSpan.textContent = basketName;
          nameSpan.title = basketName;
          button.appendChild(nameSpan);
          button.addEventListener("click", async () => {
            await runButtonAction(button, async () => {
              await executeMove({ targetBasketId: String(target.draftOrderId || target.id) });
              closeModal(moveModal);
              await refreshBaskets();
            }, (error) => {
              showMessage(message, getErrorMessage(error), "error");
            });
          });
          moveList.appendChild(button);
        });
    };
    const refreshDetails = async () => {
      if (!selectedBasketId) return;
      const requestSeq = ++detailRequestSeq;
      const requestBasketId = String(selectedBasketId);
      showMessage(message, "", "info");
      setTableLoading(tableBody, 7, "Loading basket products…");
      const details = await fetchJson(`${apiBase}/baskets/${encodeURIComponent(requestBasketId)}?includeShipping=1&includeStatus=0`);
      if (requestSeq !== detailRequestSeq || requestBasketId !== String(selectedBasketId || "")) return;
      selectedBasketDraftOrderId = String(details.basketId || selectedBasketDraftOrderId || "");
      const detailsWithPendingStatus = {
        ...details,
        items: (Array.isArray(details.items) ? details.items : []).map((item) => ({
          ...item,
          statusLoading: true,
        })),
      };
      renderItems(detailsWithPendingStatus);
      renderShipping(details.shipping);
      renderMoveTargets(details);
      startCountdown(graceNode, details.graceEndsAt, "All books are on hand. The basket will be finalized in");
      startCountdown(paymentNode, details.paymentDueAt, "Pay in");
      if (Array.isArray(details.allBaskets) && details.allBaskets.length > 0) {
        latestOpenBaskets = details.allBaskets;
      }
      renderBaskets(latestOpenBaskets);
      let statusDetails;
      try {
        statusDetails = await fetchJson(`${apiBase}/baskets/${encodeURIComponent(requestBasketId)}?statusOnly=1&includeShipping=0&includeMoveTargets=0`);
      } catch (error) {
        if (requestSeq === detailRequestSeq && requestBasketId === String(selectedBasketId || "")) {
          const failedDetails = {
            ...details,
            items: (Array.isArray(details.items) ? details.items : []).map((item) => ({
              ...item,
              statusLabel: "Unavailable",
              expectedInStore: null,
              statusLoading: false,
            })),
          };
          renderItems(failedDetails);
          showMessage(message, getErrorMessage(error), "error");
        }
        return;
      }
      if (requestSeq !== detailRequestSeq || requestBasketId !== String(selectedBasketId || "")) return;
      const statusById = new Map((Array.isArray(statusDetails.items) ? statusDetails.items : []).map((item) => [String(item.id || ""), item]));
      const mergedDetails = {
        ...details,
        ...statusDetails,
        shipping: details.shipping,
        pricing: details.pricing,
        allBaskets: details.allBaskets,
        moveTargets: details.moveTargets,
        items: (Array.isArray(details.items) ? details.items : []).map((item) => ({
          ...item,
          ...(statusById.get(String(item.id || "")) || {}),
          statusLoading: false,
        })),
      };
      renderItems(mergedDetails);
      startCountdown(graceNode, mergedDetails.graceEndsAt, "All books are on hand. The basket will be finalized in");
      startCountdown(paymentNode, mergedDetails.paymentDueAt, "Pay in");
    };
    const refreshBaskets = async () => {
      setListLoading(basketList, "Loading baskets…");
      setTableLoading(tableBody, 7, "Loading basket products…");
      const payload = await fetchJson(`${apiBase}/baskets`);
      const baskets = Array.isArray(payload.baskets) ? payload.baskets : [];
      latestSuggestedBasketName = String(payload.suggestedBasketName || "").trim();
      latestOpenBaskets = baskets;
      const hasSelected = baskets.some((basket) => getBasketRouteId(basket) === String(selectedBasketId || ""));
      if (!hasSelected) {
        selectedBasketId = null;
        selectedBasketDraftOrderId = null;
      }
      if (!selectedBasketId && baskets.length > 0) {
        const preferred = preferredDraftBasketId
          ? baskets.find((basket) => {
              const draft = getBasketDraftOrderId(basket);
              const route = getBasketRouteId(basket);
              return draft === preferredDraftBasketId || route === preferredDraftBasketId;
            })
          : null;
        const chosen = preferred || baskets[0];
        selectedBasketId = getBasketRouteId(chosen);
        selectedBasketDraftOrderId = getBasketDraftOrderId(chosen);
        if (preferred) preferredDraftBasketId = "";
      }
      renderBaskets(baskets);
      if (selectedBasketId) {
        await refreshDetails();
      } else {
        currentDetails = null;
        startCountdown(graceNode, null, "");
        startCountdown(paymentNode, null, "");
        startCountdown(pendingPaymentNode, null, "");
        if (tableBody) tableBody.innerHTML = '<tr><td colspan="7" class="open-baskets-empty">No items in this basket.</td></tr>';
        renderPricingSummary({ pricing: null }, {
          total: totalNode,
          discount: totalDiscountNode,
          codes: totalDiscountCodesNode,
        }, []);
      }
      const hasBasket = Boolean(selectedBasketDraftOrderId);
      if (finalizeButton) finalizeButton.disabled = !hasBasket;
      if (cancelButton) cancelButton.disabled = !hasBasket;
    };
    const findItemsByIds = (ids) => {
      const source = Array.isArray(currentDetails?.items) ? currentDetails.items : [];
      const byId = new Map(source.map((item) => [String(item?.id || ""), item]));
      return (Array.isArray(ids) ? ids : []).map((id) => byId.get(String(id || ""))).filter(Boolean);
    };
    const renderRemoveQuantityRows = (items) => {
      if (!removeQuantityList) return;
      removeQuantityList.innerHTML = "";
      items.forEach((item, index) => {
        const itemId = String(item?.id || "").trim();
        if (!itemId) return;
        const maxQty = Math.max(1, Math.floor(Number(item?.quantity || 1)));
        const title = String(item?.title || "Item").trim();
        const variant = String(item?.variantTitle || "").trim();
        const itemText = variant ? `${title} (${variant})` : title;
        const row = document.createElement("div");
        row.className = "open-baskets-modal__quantity-row open-baskets-modal__quantity-row--remove";
        const inputId = `DashboardRemoveQty-${itemId}-${index}`;
        row.innerHTML = `
          <label class="open-baskets-modal__quantity-label open-baskets-modal__quantity-label--item" for="${inputId}">${escapeHtml(itemText)}</label>
          <span class="open-baskets-modal__quantity-text">Quantity:</span>
          <input id="${inputId}" class="field__input open-baskets-modal__quantity-input" type="number" min="1" step="1" max="${maxQty}" value="${maxQty}" data-dashboard-remove-quantity data-item-id="${escapeHtml(itemId)}" data-max-quantity="${maxQty}">
        `;
        removeQuantityList.appendChild(row);
      });
    };
    const collectRemoveQuantities = () => {
      const quantityMap = {};
      if (!removeQuantityList) return quantityMap;
      const inputs = Array.from(removeQuantityList.querySelectorAll("[data-dashboard-remove-quantity]"));
      inputs.forEach((input) => {
        const itemId = String(input.getAttribute("data-item-id") || "").trim();
        const maxQty = Math.max(1, Math.floor(Number(input.getAttribute("data-max-quantity") || 1)));
        const requested = Math.floor(Number(input.value || maxQty));
        if (!itemId) return;
        if (!Number.isFinite(requested) || requested <= 0) {
          throw new Error("Each quantity must be a positive number.");
        }
        quantityMap[itemId] = Math.min(maxQty, requested);
      });
      return quantityMap;
    };
    const showRemoveAllWarning = () => {
      if (removeWarningModal && typeof removeWarningModal.showModal === "function") {
        removeWarningModal.showModal();
      } else {
        showMessage(message, "You can't remove all items. Cancel the basket instead.", "error");
      }
    };
    const postBasketAction = async (path) => {
      if (!selectedBasketDraftOrderId) throw new Error("Please select a basket first.");
      await fetchJson(`${apiBase}${path}`, {
        method: "POST",
        headers: jsonHeaders,
        body: JSON.stringify({ basketId: selectedBasketDraftOrderId }),
      });
    };
    if (moveButton) {
      moveButton.addEventListener("click", async () => {
        if (!selectedBasketDraftOrderId || selectedItemIds.length === 0 || !moveModal) return;
        if (moveNewWrap) moveNewWrap.hidden = true;
        if (moveNewToggleButton) moveNewToggleButton.hidden = false;
        if (moveNewNameInput) moveNewNameInput.value = "";
        if (typeof moveModal.showModal === "function") moveModal.showModal();
      });
    }
    if (moveNewToggleButton) {
      moveNewToggleButton.addEventListener("click", () => {
        if (!moveNewWrap) return;
        moveNewWrap.hidden = false;
        moveNewToggleButton.hidden = true;
        if (moveNewNameInput) {
          moveNewNameInput.value = defaultBasketName(latestMoveTargets, latestSuggestedBasketName);
          moveNewNameInput.focus();
          moveNewNameInput.select();
        }
      });
    }
    if (moveNewNameInput) {
      moveNewNameInput.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          if (moveNewConfirmButton) moveNewConfirmButton.click();
        }
        if (event.key === "Escape") {
          event.preventDefault();
          if (moveNewWrap) moveNewWrap.hidden = true;
          if (moveNewToggleButton) moveNewToggleButton.hidden = false;
          moveNewNameInput.value = "";
        }
      });
    }
    if (moveNewConfirmButton) {
      moveNewConfirmButton.addEventListener("click", async () => {
        const newBasketName = moveNewNameInput ? String(moveNewNameInput.value || "").trim() : "";
        if (!newBasketName) {
          showMessage(message, "Please enter a name for the new basket.", "error");
          return;
        }
        await runButtonAction(moveNewConfirmButton, async () => {
          await executeMove({ targetBasketName: newBasketName });
          closeModal(moveModal);
          if (moveNewNameInput) moveNewNameInput.value = "";
          if (moveNewWrap) moveNewWrap.hidden = true;
          if (moveNewToggleButton) moveNewToggleButton.hidden = false;
          await refreshBaskets();
          invalidateTabsData();
        }, (error) => {
          if (showDuplicateBasketNamePopup(error)) return;
          showMessage(message, getErrorMessage(error), "error");
        });
      });
    }
    if (removeButton) {
      removeButton.addEventListener("click", () => {
        updateSelection();
        if (!selectedBasketDraftOrderId || selectedItemIds.length === 0) {
          showMessage(message, "Please select at least one product to remove.", "error");
          return;
        }
        pendingRemoveItemIds = [...selectedItemIds];
        renderRemoveQuantityRows(findItemsByIds(pendingRemoveItemIds));
        if (removeModal && typeof removeModal.showModal === "function") {
          removeModal.showModal();
          return;
        }
        showMessage(message, "Please confirm item removal.", "info");
      });
    }
    if (removeConfirmButton) {
      removeConfirmButton.addEventListener("click", async () => {
        if (!selectedBasketDraftOrderId || pendingRemoveItemIds.length === 0) {
          showMessage(message, "Please select at least one product to remove.", "error");
          closeModal(removeModal);
          return;
        }
        await runButtonAction(removeConfirmButton, async () => {
          const removeQuantities = collectRemoveQuantities();
          await fetchJson(`${apiBase}/baskets/items/remove`, {
            method: "POST",
            headers: jsonHeaders,
            body: JSON.stringify({
              basketId: selectedBasketDraftOrderId,
              itemIds: pendingRemoveItemIds,
              removeQuantities,
            }),
          });
          pendingRemoveItemIds = [];
          if (removeQuantityList) removeQuantityList.innerHTML = "";
          await refreshDetails();
          closeModal(removeModal);
        }, (error) => {
          const errorText = getErrorMessage(error, String(error));
          if (errorText.includes("You can't remove all items. Cancel the basket instead.")) {
            showRemoveAllWarning();
          } else {
            showMessage(message, errorText, "error");
          }
        }, "open-baskets-modal__button--loading").finally(() => {
          removeConfirmButton.disabled = false;
          updateSelection();
        });
      });
    }
    if (removeWarningCloseButton) {
      removeWarningCloseButton.addEventListener("click", () => closeModal(removeWarningModal));
    }
    if (finalizeButton && finalizeModal) {
      finalizeButton.addEventListener("click", () => {
        if (typeof finalizeModal.showModal === "function") finalizeModal.showModal();
      });
    }
    if (cancelButton && cancelModal) {
      cancelButton.addEventListener("click", () => {
        if (typeof cancelModal.showModal === "function") cancelModal.showModal();
      });
    }
    modalCloseButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const modal = button.closest("dialog");
        if (modal && typeof modal.close === "function") modal.close();
        if (modal === moveModal) {
          if (moveNewWrap) moveNewWrap.hidden = true;
          if (moveNewToggleButton) moveNewToggleButton.hidden = false;
          if (moveNewNameInput) moveNewNameInput.value = "";
        }
        if (modal === removeModal) {
          pendingRemoveItemIds = [];
          if (removeQuantityList) removeQuantityList.innerHTML = "";
        }
      });
    });
    // tab and resend listeners are attached once below
    if (finalizeConfirmButton) {
      finalizeConfirmButton.addEventListener("click", async () => {
        await runButtonAction(finalizeConfirmButton, async () => {
          await postBasketAction("/baskets/finalize");
          closeModal(finalizeModal);
          await refreshBaskets();
          invalidateTabsData();
        }, (error) => {
          showMessage(message, getErrorMessage(error), "error");
        });
      });
    }
    if (cancelConfirmButton) {
      cancelConfirmButton.addEventListener("click", async () => {
        await runButtonAction(cancelConfirmButton, async () => {
          await postBasketAction("/baskets/cancel");
          closeModal(cancelModal);
          if (activeTab === "open") {
            await refreshBaskets();
          } else {
            invalidateTabsData();
            await loadTabsData({ force: true });
          }
        }, (error) => {
          showMessage(message, getErrorMessage(error), "error");
        });
      });
    }
    tabButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const tabName = String(button.getAttribute("data-dashboard-tab") || "open");
        void switchTab(tabName).catch((error) => showMessage(message, getErrorMessage(error), "error"));
      });
    });
    if (resendPaymentButton) {
      resendPaymentButton.addEventListener("click", async () => {
        if (!selectedArchiveBasket.pending) return;
        const selectedPending = getTabRows("pending").find((row) => String(row.id) === String(selectedArchiveBasket.pending));
        const pendingDraftOrderId = selectedPending?.draftOrderId ? String(selectedPending.draftOrderId) : "";
        if (!pendingDraftOrderId) return;
        await runButtonAction(resendPaymentButton, async () => {
          await fetchJson(`${apiBase}/baskets/resend-invoice`, {
            method: "POST",
            headers: jsonHeaders,
            body: JSON.stringify({ basketId: pendingDraftOrderId }),
          });
          await loadArchiveDetails("pending");
          showMessage(message, "Payment link resent.", "success");
        }, (error) => {
          showMessage(message, getErrorMessage(error), "error");
        }).finally(() => {
          resendPaymentButton.disabled = !selectedArchiveBasket.pending;
        });
      });
    }
    if (pendingCancelButton && pendingCancelModal) {
      pendingCancelButton.addEventListener("click", () => {
        if (!selectedArchiveBasket.pending) {
          showMessage(message, "Please select a basket first.", "error");
          return;
        }
        const selectedPending = getTabRows("pending").find((row) => String(row.id) === String(selectedArchiveBasket.pending));
        const pendingDraftOrderId = selectedPending?.draftOrderId ? String(selectedPending.draftOrderId) : "";
        if (!pendingDraftOrderId) {
          showMessage(message, "Please select a basket first.", "error");
          return;
        }
        selectedBasketDraftOrderId = pendingDraftOrderId;
        if (pendingCancelModal && typeof pendingCancelModal.showModal === "function") {
          pendingCancelModal.showModal();
        }
      });
    }
    if (pendingCancelConfirmButton) {
      pendingCancelConfirmButton.addEventListener("click", async () => {
        await runButtonAction(pendingCancelConfirmButton, async () => {
          await postBasketAction("/baskets/cancel");
          closeModal(pendingCancelModal);
          invalidateTabsData();
          await loadTabsData({ force: true });
        }, (error) => {
          showMessage(message, getErrorMessage(error), "error");
        });
      });
    }
    sortableHeaders.forEach((header) => {
      const key = String(header.getAttribute("data-sort-key") || "");
      const panel = header.closest("[data-dashboard-panel]");
      const tabName = String(panel?.getAttribute("data-dashboard-panel") || "open");
      header.addEventListener("click", () => applySort(key, tabName));
      header.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        applySort(key, tabName);
      });
    });
    updateSortHeaders();
    void switchTab("open").catch((error) => showMessage(message, getErrorMessage(error), "error"));
  }
  document.addEventListener("DOMContentLoaded", () => {
    initReserveWidgets();
    initDashboard();
  });
})();
